import React, { useState, useEffect } from 'react';
import {
  Flex,
  Typography,
  Button,
  Card,
  Table,
  Tag,
  Avatar,
  Tabs,
  Spin,
  Skeleton,
  Row,
  Col,
  Segmented,
  Pagination,
  Space
} from 'antd';
import {
  ArrowLeftOutlined,
  ShopOutlined,
  TeamOutlined,
  CarOutlined,
  RiseOutlined,
  BankOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardService from '../../service';
import LotCard from '../../components/LotCard';
import VinCopy from '../../components/VinCopy';
import CreatorInfo from '../../components/CreatorInfo';
import { useNotification } from '../../hooks/useNotification';

const ManagerDetail = () => {
  const { managerId, lang } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [manager, setManager] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [subdealers, setSubdealers] = useState([]);
  const [purchasedCars, setPurchasedCars] = useState([]);
  const [activeTab, setActiveTab] = useState('0');
  const [loading, setLoading] = useState(true);
  const [winCars, setWinCars] = useState([]);
  const [bidViewMode, setBidViewMode] = useState('grouped');
  const [groupedBids, setGroupedBids] = useState([]);
  const [isBidsGrouped, setIsBidsGrouped] = useState(false);
  const [managerStats, setManagerStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Pagination state for each role
  const [dealersPagination, setDealersPagination] = useState({});
  const [subdealersPagination, setSubdealersPagination] = useState({});

  // Current page for each role
  const [dealersPage, setDealersPage] = useState(1);
  const [subdealersPage, setSubdealersPage] = useState(1);

  // Loading state for pagination
  const [paginationLoading, setPaginationLoading] = useState(false);

  // Load functions for each role with pagination
  const loadDealers = async (page = 1) => {
    if (!managerId) return;
    try {
      setPaginationLoading(true);
      const dealersResponse = await DashboardService.getUsersByRole('dealer', { managerId }, page, 100);
      if (dealersResponse.success && dealersResponse.users) {
        setDealers(dealersResponse.users);
        setDealersPagination(dealersResponse.pagination || {});
        setDealersPage(page);
      }
    } catch (error) {
      console.error('Error loading dealers:', error);
    } finally {
      setPaginationLoading(false);
    }
  };

  const loadSubdealers = async (page = 1) => {
    if (!managerId) return;
    try {
      setPaginationLoading(true);
      const subdealersResponse = await DashboardService.getUsersByRole('subdealer', { managerId }, page, 100);
      if (subdealersResponse.success && subdealersResponse.users) {
        setSubdealers(subdealersResponse.users);
        setSubdealersPagination(subdealersResponse.pagination || {});
        setSubdealersPage(page);
      }
    } catch (error) {
      console.error('Error loading subdealers:', error);
    } finally {
      setPaginationLoading(false);
    }
  };

  useEffect(() => {
    const loadManagerData = async () => {
      try {
        setLoading(true);

        const managerResponse = await DashboardService.getUserById(managerId);

        if (managerResponse.success && managerResponse.user) {
          const managerData = managerResponse.user;
          setManager(managerData);

          const dealersResponse = await DashboardService.getUsersByRole('dealer', { managerId }, 1, 100);
          if (dealersResponse.success && dealersResponse.users) {
            setDealers(dealersResponse.users);
            setDealersPagination(dealersResponse.pagination || {});
          }

          const subdealersResponse = await DashboardService.getUsersByRole('subdealer', { managerId }, 1, 100);
          if (subdealersResponse.success && subdealersResponse.users) {
            setSubdealers(subdealersResponse.users);
            setSubdealersPagination(subdealersResponse.pagination || {});
          }

          if (managerData.branchId) {
            try {
              const purchasedLotsResponse = await DashboardService.getBranchPurchasedLots(managerData.branchId, { page: 1, limit: 1000 });
              if (purchasedLotsResponse?.success && purchasedLotsResponse.lots) {
                setPurchasedCars(purchasedLotsResponse.lots);
              } else if (purchasedLotsResponse?.lots) {
                setPurchasedCars(purchasedLotsResponse.lots);
              }
            } catch (error) {
              console.error('Error loading purchased lots:', error);
            }
          }

          loadManagerStats(managerData);
        }
      } catch (error) {
        console.error('Error loading manager data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (managerId) {
      loadManagerData();
    }
  }, [managerId]);

  const loadManagerStats = async (managerData) => {
    if (!managerId) return;
    try {
      setStatsLoading(true);
      if (managerData?.branchId) {
        const dashboardResponse = await DashboardService.getManagerDashboard(managerId, managerData.branchId);
        if (dashboardResponse.success && dashboardResponse.dashboard) {
          setManagerStats(dashboardResponse.dashboard.overview);
          if (dashboardResponse.dashboard.recentActivity?.allWins) {
            setWinCars(dashboardResponse.dashboard.recentActivity.allWins);
          }
          return;
        }
      }
      const statsResponse = await DashboardService.getBidStats('manager', managerId);
      if (statsResponse.success) {
        setManagerStats(statsResponse.stats);
      }
    } catch (error) {
      console.error('Error loading manager stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/${lang}/managers`);
  };

  const handleDealerClick = (dealer) => {
    const dealerId = dealer.user_id;
    if (dealerId) {
      navigate(`/${lang}/dealers/${dealerId}`);
    } else {
      console.error('No valid ID found for dealer:', dealer);
    }
  };

  const handleDeleteDealer = async (dealer, e) => {
    e.stopPropagation();

    if (!window.confirm(`Are you sure you want to delete dealer "${dealer.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const dealerId = dealer.user_id;
      const result = await DashboardService.deleteUser(dealerId);

      if (result.success) {
        showSuccess('Dealer deleted successfully!');
        loadDealers(dealersPage);
      } else {
        showError(result.error || 'Failed to delete dealer');
      }
    } catch (error) {
      console.error('Error deleting dealer:', error);
      showError(error.message || 'Failed to delete dealer. Please try again.');
    }
  };

  const handleDeleteSubdealer = async (subdealer, e) => {
    e.stopPropagation();

    if (!window.confirm(`Are you sure you want to delete subdealer "${subdealer.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const subdealerId = subdealer.user_id;
      const result = await DashboardService.deleteUser(subdealerId);

      if (result.success) {
        showSuccess('Subdealer deleted successfully!');
        loadSubdealers(subdealersPage);
      } else {
        showError(result.error || 'Failed to delete subdealer');
      }
    } catch (error) {
      console.error('Error deleting subdealer:', error);
      showError(error.message || 'Failed to delete subdealer. Please try again.');
    }
  };

  const loadManagerBids = async () => {
    if (!managerId) return;
    try {
      const groupByLot = bidViewMode === 'grouped';
      const response = await DashboardService.getManagerBids(managerId, {
        page: 1,
        limit: 50,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        groupByLot
      });

      if (response.success !== false) {
        const bidsData = response.lots || response.bids || [];
        setGroupedBids(bidsData);
        setIsBidsGrouped(response.grouped || false);
      }
    } catch (error) {
      console.error('Error loading manager bids:', error);
      setGroupedBids([]);
    }
  };

  const handleBidViewModeChange = (value) => {
    if (value !== null) {
      setBidViewMode(value);
    }
  };

  useEffect(() => {
    if (activeTab === '2' && managerId) {
      loadManagerBids();
    }
  }, [bidViewMode, activeTab, managerId]);

  useEffect(() => {
    if (activeTab === '3' && managerId) {
      loadWinCars();
    }
  }, [activeTab, managerId]);

  const loadWinCars = async () => {
    if (winCars.length > 0) return;
    if (!managerId) return;
    try {
      const response = await DashboardService.getUserWinningBids(managerId);
      if (response.success && response.winningLots) {
        setWinCars(response.winningLots);
      } else if (response.winningLots) {
        setWinCars(response.winningLots);
      }
    } catch (error) {
      console.error('Error loading winning lots:', error);
      setWinCars([]);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 2 }} style={{ marginBottom: 24 }} />
        <Flex gap={16} style={{ marginBottom: 24 }}>
          <Skeleton.Avatar active size={64} />
          <div style={{ flex: 1 }}>
            <Skeleton.Input active block style={{ marginBottom: 8 }} />
            <Skeleton.Input active block size="small" />
          </div>
        </Flex>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {[1, 2, 3, 4].map((i) => (
            <Col xs={24} sm={12} md={6} key={i}>
              <Card><Skeleton active /></Card>
            </Col>
          ))}
        </Row>
        <Skeleton.Button active block style={{ marginBottom: 16 }} />
        <Card><Skeleton active paragraph={{ rows: 5 }} /></Card>
      </div>
    );
  }

  if (!manager) {
    return (
      <div style={{ padding: 24, textAlign: 'center', marginTop: 48 }}>
        <Typography.Title level={4} type="danger">Manager not found</Typography.Title>
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          The requested manager could not be found or you don't have access.
        </Typography.Text>
        <Button type="primary" icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back to Managers
        </Button>
      </div>
    );
  }

  const branchName = manager.branchName || 'Unknown Branch';

  const stats = [
    {
      title: 'Total Dealers',
      value: dealersPagination.totalUsers || dealers.length,
      icon: <ShopOutlined />,
      color: '#2196f3'
    },
    {
      title: 'Total Subdealers',
      value: subdealersPagination.totalUsers || subdealers.length,
      icon: <TeamOutlined />,
      color: '#4caf50'
    },
    {
      title: 'Total Bids',
      value: managerStats?.totalBids || groupedBids.length,
      icon: <RiseOutlined />,
      color: '#ff9800',
      subtitle: managerStats?.activeBids ? `${managerStats.activeBids} active` : ''
    },
    {
      title: 'Purchased Cars',
      value: purchasedCars.length,
      icon: <CarOutlined />,
      color: '#9c27b0',
      subtitle: managerStats?.wonAuctions ? `${managerStats.wonAuctions} won` : ''
    }
  ];

  const dealerColumns = [
    {
      title: 'Dealer',
      key: 'dealer',
      render: (_, record) => (
        <Flex align="center" gap={8}>
          <Avatar style={{ backgroundColor: '#1e3a5f' }}>
            {record.avatar || record.name?.substring(0, 2).toUpperCase() || 'D'}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>@{record.user_name}</Typography.Text>
          </div>
        </Flex>
      )
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div>{record.phone}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Typography.Text>
        </div>
      )
    },
    {
      title: 'Subdealers',
      key: 'subdealers',
      render: (_, record) => (
        <Tag icon={<TeamOutlined />} color="blue">
          {record.stats?.totalSubdealers || 0}
        </Tag>
      )
    },
    {
      title: 'Active Bids',
      dataIndex: ['stats', 'activeBids'],
      key: 'activeBids',
      render: (v) => v || 0
    },
    {
      title: 'Won Auctions',
      dataIndex: ['stats', 'wonAuctions'],
      key: 'wonAuctions',
      render: (v) => v || 0
    },
    {
      title: 'Total Spent',
      dataIndex: ['stats', 'totalSpent'],
      key: 'totalSpent',
      render: (v) => <span style={{ fontWeight: 600 }}>{v || '$0'}</span>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={(e) => handleDeleteDealer(record, e)}
          title="Delete Dealer"
        />
      )
    }
  ];

  const subdealerColumns = [
    {
      title: 'Subdealer',
      key: 'subdealer',
      render: (_, record) => (
        <Flex align="center" gap={8}>
          <Avatar style={{ backgroundColor: '#8b5cf6' }}>
            {record.avatar || record.name?.substring(0, 2).toUpperCase() || 'S'}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600 }}>{record.name}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>@{record.user_name}</Typography.Text>
          </div>
        </Flex>
      )
    },
    {
      title: 'Dealer',
      key: 'dealer',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <ShopOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
          <Typography.Text>{record.dealerName}</Typography.Text>
        </Flex>
      )
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          <div>{record.phone}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Typography.Text>
        </div>
      )
    },
    {
      title: 'Active Bids',
      dataIndex: ['stats', 'activeBids'],
      key: 'activeBids',
      render: (v) => v || 0
    },
    {
      title: 'Won Auctions',
      dataIndex: ['stats', 'wonAuctions'],
      key: 'wonAuctions',
      render: (v) => v || 0
    },
    {
      title: 'Total Spent',
      dataIndex: ['stats', 'totalSpent'],
      key: 'totalSpent',
      render: (v) => <span style={{ fontWeight: 600 }}>{v || '$0'}</span>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={(e) => handleDeleteSubdealer(record, e)}
          title="Delete Subdealer"
        />
      )
    }
  ];

  const bidColumns = [
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      key: 'lotNumber',
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>
    },
    {
      title: 'VIN',
      key: 'vin',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <Typography.Text type="secondary">{record.vin || 'N/A'}</Typography.Text>
          {record.vin && <VinCopy vin={record.vin} size="small" />}
        </Flex>
      )
    },
    {
      title: 'Bid Amount',
      key: 'bidAmount',
      render: (_, record) => (
        <span style={{ fontWeight: 600, color: '#1e3a5f' }}>
          ${((record.bidAmount || record.bid) || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: 'User',
      dataIndex: 'appUserName',
      key: 'user',
      render: (v) => v || 'Unknown'
    },
    {
      title: 'Event',
      dataIndex: 'event',
      key: 'event',
      render: (v) => v ? <Tag>{v}</Tag> : 'N/A'
    },
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'time',
      render: (v) => v ? new Date(v).toLocaleString() : 'N/A'
    }
  ];

  const winCarsColumns = [
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      key: 'lotNumber',
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>
    },
    {
      title: 'VIN',
      key: 'vin',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <Typography.Text type="secondary">{record.vin || 'N/A'}</Typography.Text>
          {record.vin && <VinCopy vin={record.vin} size="small" />}
        </Flex>
      )
    },
    {
      title: 'Host',
      dataIndex: 'host',
      key: 'host',
      render: (v) => v ? <Tag color="blue">{v}</Tag> : 'N/A'
    },
    {
      title: 'Current Bid',
      key: 'currentBid',
      render: (_, record) => (
        <span style={{ fontWeight: 600, color: '#22c55e' }}>
          ${(record.bidAmount || record.bid || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: 'Bidder',
      key: 'bidder',
      render: (_, record) => (
        <div>
          <div>{record.appUserName || 'N/A'}</div>
          {record.buyerNumber && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>#{record.buyerNumber}</Typography.Text>
          )}
        </div>
      )
    },
    {
      title: 'Last Update',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (v) => v ? new Date(v).toLocaleString() : 'N/A'
    },
    {
      title: 'Times Updated',
      dataIndex: 'updatedCount',
      key: 'updatedCount',
      render: (v) => (
        <Tag color={v > 5 ? 'warning' : v > 0 ? 'blue' : 'default'} title={`Bid updated ${v || 0} times`}>
          {v || 0}
        </Tag>
      )
    }
  ];

  const purchasedColumns = [
    {
      title: 'Image',
      key: 'image',
      render: (_, record) => {
        const carImage = record.images?.length > 0 ? record.images[0].url : null;
        return (
          <div style={{ width: 80, height: 60, position: 'relative' }}>
            {carImage && (
              <img
                src={carImage}
                alt={`${record.year} ${record.make} ${record.model}`}
                style={{
                  width: 80,
                  height: 60,
                  objectFit: 'cover',
                  borderRadius: 4,
                  border: '1px solid #d9d9d9'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                }}
              />
            )}
            <div
              style={{
                width: 80,
                height: 60,
                display: carImage ? 'none' : 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5',
                borderRadius: 4,
                border: '1px solid #d9d9d9'
              }}
            >
              <CarOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />
            </div>
          </div>
        );
      }
    },
    {
      title: 'Lot Number',
      key: 'lotNumber',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.lotNumber}</div>
          {record.gatePassPin && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>PIN: {record.gatePassPin}</Typography.Text>
          )}
        </div>
      )
    },
    {
      title: 'Vehicle',
      key: 'vehicle',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.year} {record.make} {record.model}</div>
          {record.titleStatus && <Tag>{record.titleStatus}</Tag>}
        </div>
      )
    },
    {
      title: 'VIN',
      key: 'vin',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <Typography.Text type="secondary">{record.vin || 'N/A'}</Typography.Text>
          {record.vin && <VinCopy vin={record.vin} size="small" />}
        </Flex>
      )
    },
    {
      title: 'Purchase Price',
      key: 'purchasePrice',
      render: (_, record) => (
        <span style={{ fontWeight: 600 }}>
          ${(record.purchasePriceInDollars || record.purchasePrice || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: 'Fees',
      key: 'fees',
      render: (_, record) => (
        <span>
          ${(record.purchasePriceFeeInDollars || record.purchasePriceFee || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: 'Total',
      key: 'total',
      render: (_, record) => {
        const total = (record.purchasePriceInDollars || record.purchasePrice || 0) + (record.purchasePriceFeeInDollars || record.purchasePriceFee || 0);
        return <span style={{ fontWeight: 700, color: '#1e3a5f' }}>${total.toLocaleString()}</span>;
      }
    },
    {
      title: 'Unpaid',
      key: 'unpaid',
      render: (_, record) => {
        const unpaid = record.unpaidAmountInDollars || record.unpaidAmount || 0;
        return (
          <span style={{ fontWeight: 700, color: unpaid > 0 ? '#ff4d4f' : '#22c55e' }}>
            ${unpaid.toLocaleString()}
          </span>
        );
      }
    },
    {
      title: 'Buyer',
      dataIndex: ['userName'],
      key: 'buyer',
      render: (v, record) => v || record.appUserName || 'N/A'
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      render: (v) => v || 'N/A'
    }
  ];

  const tabItems = [
    {
      key: '0',
      label: `Dealers (${dealersPagination.totalUsers || dealers.length})`,
      children: (
        <div style={{ position: 'relative' }}>
          {paginationLoading && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.7)', zIndex: 1
            }}>
              <Spin size="large" />
            </div>
          )}
          <Table
            columns={dealerColumns}
            dataSource={dealers}
            rowKey={(r) => r.id || r.user_id}
            pagination={false}
            onRow={(record) => ({
              onClick: () => handleDealerClick(record),
              style: { cursor: 'pointer' }
            })}
            locale={{ emptyText: 'No dealers found' }}
          />
          {dealersPagination.totalUsers > 0 && (
            <Flex justify="center" align="center" style={{ marginTop: 16 }} vertical gap={8}>
              {dealersPagination.totalPages > 1 && (
                <Pagination
                  total={dealersPagination.totalUsers}
                  current={dealersPage}
                  pageSize={100}
                  onChange={loadDealers}
                  disabled={paginationLoading}
                />
              )}
              <Typography.Text type="secondary">
                Showing {dealers.length} of {dealersPagination.totalUsers} total
              </Typography.Text>
            </Flex>
          )}
        </div>
      )
    },
    {
      key: '1',
      label: `Subdealers (${subdealersPagination.totalUsers || subdealers.length})`,
      children: (
        <div style={{ position: 'relative' }}>
          {paginationLoading && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.7)', zIndex: 1
            }}>
              <Spin size="large" />
            </div>
          )}
          <Table
            columns={subdealerColumns}
            dataSource={subdealers}
            rowKey={(r) => r.id || r.user_id}
            pagination={false}
            locale={{ emptyText: 'No subdealers found' }}
          />
          {subdealersPagination.totalUsers > 0 && (
            <Flex justify="center" align="center" style={{ marginTop: 16 }} vertical gap={8}>
              {subdealersPagination.totalPages > 1 && (
                <Pagination
                  total={subdealersPagination.totalUsers}
                  current={subdealersPage}
                  pageSize={100}
                  onChange={loadSubdealers}
                  disabled={paginationLoading}
                />
              )}
              <Typography.Text type="secondary">
                Showing {subdealers.length} of {subdealersPagination.totalUsers} total
              </Typography.Text>
            </Flex>
          )}
        </div>
      )
    },
    {
      key: '2',
      label: `Bids (${managerStats?.totalBids || groupedBids.length})`,
      children: (
        <div>
          <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>Manager Bids</Typography.Title>
            <Segmented
              value={bidViewMode}
              onChange={handleBidViewModeChange}
              options={[
                { value: 'grouped', label: <><AppstoreOutlined /> Grouped by Lot</> },
                { value: 'flat', label: <><UnorderedListOutlined /> Flat List</> }
              ]}
            />
          </Flex>

          {isBidsGrouped && bidViewMode === 'grouped' ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              {groupedBids.length > 0 ? (
                groupedBids.map((lot, index) => (
                  <LotCard key={lot.lotNumber || index} lot={lot} />
                ))
              ) : (
                <Card>
                  <Typography.Text type="secondary">No bids found</Typography.Text>
                </Card>
              )}
            </Space>
          ) : (
            <Table
              columns={bidColumns}
              dataSource={groupedBids}
              rowKey={(r, i) => r._id || i}
              pagination={false}
              locale={{ emptyText: 'No bids found' }}
            />
          )}
        </div>
      )
    },
    {
      key: '3',
      label: `Winning Lots (${winCars.length})`,
      children: (
        <div>
          <Typography.Title level={5} style={{ marginBottom: 8 }}>Currently Winning Lots</Typography.Title>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            These are auctions where users under your management are currently the highest bidders. Note: Auctions may still be active.
          </Typography.Text>
          <Table
            columns={winCarsColumns}
            dataSource={winCars}
            rowKey={(r) => r._id || r.lotNumber}
            pagination={false}
            locale={{
              emptyText: 'No winning lots yet. Winning lots are auctions where you\'re currently the highest bidder.'
            }}
          />
        </div>
      )
    },
    {
      key: '4',
      label: `Purchased Cars (${purchasedCars.length})`,
      children: (
        <div>
          <Typography.Title level={5} style={{ marginBottom: 8 }}>Purchased Cars</Typography.Title>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            These are cars won by your branch. Auctions are complete and payment is required.
          </Typography.Text>
          <Table
            columns={purchasedColumns}
            dataSource={purchasedCars}
            rowKey={(r) => r._id || r.lotNumber}
            pagination={false}
            locale={{
              emptyText: 'No purchased cars yet. Purchased cars are vehicles won at auction that need payment.'
            }}
          />
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ marginBottom: 16 }}>
          Back to Managers
        </Button>

        <Flex align="center" gap={16} style={{ marginBottom: 16 }}>
          <Avatar size={64} style={{ backgroundColor: '#1e3a5f', fontSize: 24 }}>
            {manager.avatar || manager.name?.substring(0, 2).toUpperCase() || 'MG'}
          </Avatar>
          <div>
            <Typography.Title level={3} style={{ margin: 0 }}>{manager.name}</Typography.Title>
            <Flex align="center" gap={8} style={{ marginTop: 4 }}>
              <Tag icon={<BankOutlined />} color="blue">{branchName}</Tag>
              <Typography.Text type="secondary">@{manager.user_name}</Typography.Text>
            </Flex>
          </div>
        </Flex>

        <Flex gap={16} style={{ marginBottom: 8 }}>
          <Typography.Text type="secondary">📧 {manager.email}</Typography.Text>
          <Typography.Text type="secondary">📱 {manager.phone}</Typography.Text>
        </Flex>

        {(manager.createdBy || manager.createdAt) && (
          <div style={{ marginTop: 16 }}>
            <CreatorInfo createdBy={manager.createdBy} createdAt={manager.createdAt} />
          </div>
        )}
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {statsLoading ? (
          <Col span={24}>
            <Flex justify="center" align="center" style={{ padding: 48 }}>
              <Spin size="large" />
              <Typography.Text style={{ marginLeft: 16 }}>Loading statistics...</Typography.Text>
            </Flex>
          </Col>
        ) : (
          stats.map((stat, index) => (
            <Col xs={24} sm={12} md={6} key={index}>
              <Card size="small">
                <Flex align="center" gap={8}>
                  <span style={{ color: stat.color, fontSize: 20 }}>{stat.icon}</span>
                  <div>
                    <Typography.Title level={4} style={{ margin: 0 }}>{stat.value}</Typography.Title>
                    <Typography.Text type="secondary">{stat.title}</Typography.Text>
                    {stat.subtitle && (
                      <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                        {stat.subtitle}
                      </Typography.Text>
                    )}
                  </div>
                </Flex>
              </Card>
            </Col>
          ))
        )}
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>
    </div>
  );
};

export default ManagerDetail;
